import { LUNCH, OFFICE_CLOSED, OFFICE_OPEN } from '@/config/constants'
import {
	SET_OFFICE_OPEN_INFO,
	SET_ALERT_SETTING_POPUP,
	SET_ALERT_PREFERENCES,
} from '@/store/mutation-types'
import { api } from '@/store/helpers'
import moment from 'moment-timezone'
import { INITIAL_OFFICE_TIMING_INFO } from './state'

let scheduledTimeout = null

export default {
	async getOfficestatus({ commit, rootGetters, dispatch }, payload) {
		const activePartner = rootGetters['partners/active']

		if (!activePartner) {
			return
		}

		const { timezone: partnerTimeZone, id: activePartnerId } = activePartner

		const getMomentDateTime = time => {
			if (!time) return null
			const today = moment()
				.tz(partnerTimeZone)
				.format('YYYY-MM-DD')
			return moment.tz(
				`${today} ${time}`,
				'YYYY-MM-DD HH:mm',
				partnerTimeZone
			)
		}

		const normalizeTimeRange = (start, end) => {
			if (start && end && !end.isAfter(start)) {
				end.add(1, 'day')
			}
		}

		try {
			// Always fetch latest schedule
			let officeTimingsData = []
			let officeOpenInfo = INITIAL_OFFICE_TIMING_INFO

			if (!payload || !payload.length) {
				const response = await api()
					.partners(activePartnerId)
					.schedules()
					.get()
				officeTimingsData = response.get('data', [])
			} else {
				officeTimingsData = payload
			}

			// Hide office status if no timings are set
			const hasSomeTimings = officeTimingsData?.some(
				d => d?.starts_at && d?.ends_at
			)
			if (hasSomeTimings) {
				const hasLunchTimings = officeTimingsData?.some(
					d => d?.lunch_starts_at && d?.lunch_ends_at
				)
				officeOpenInfo = {
					...officeOpenInfo,
					visible: true,
					officeHoursSet: true,
					lunchHoursSet: hasLunchTimings,
				}
			}

			const now = moment().tz(partnerTimeZone)
			const currentDay = now.format('dddd').toLowerCase()
			const timings = officeTimingsData?.find(
				d => d?.day_name?.toLowerCase() === currentDay
			)

			if (timings?.is_open) {
				const start = getMomentDateTime(timings.starts_at)
				const end = getMomentDateTime(timings.ends_at)
				const lunchStart = getMomentDateTime(timings.lunch_starts_at)
				const lunchEnd = getMomentDateTime(timings.lunch_ends_at)
				// Handle overnight shifts
				normalizeTimeRange(start, end)
				normalizeTimeRange(lunchStart, lunchEnd)

				const nextMidnight = moment()
					.tz(partnerTimeZone)
					.add(1, 'day')
					.startOf('day')

				// Immediately update office status for current time
				if (now.isBetween(start, end, null, '[]')) {
					if (now.isBetween(lunchStart, lunchEnd, null, '[]')) {
						officeOpenInfo = {
							...officeOpenInfo,
							active: false,
							indicator: LUNCH,
						}
					} else {
						officeOpenInfo = {
							...officeOpenInfo,
							active: true,
							indicator: OFFICE_OPEN,
						}
					}
				}

				// Clear old timers before scheduling new ones
				if (scheduledTimeout) {
					clearTimeout(scheduledTimeout)
					scheduledTimeout = null
				}

				const delayArr = [
					start,
					lunchStart,
					lunchEnd,
					end,
					nextMidnight,
				]?.map(time => {
					if (!time) return -1
					return time.diff(now)
				})

				const delay = delayArr
					?.filter(i => i > 0)
					.sort((a, b) => a - b)[0]

				if (delay > 0) {
					scheduledTimeout = setTimeout(() => {
						dispatch('getOfficestatus')
					}, delay)
				}
			}

			commit(SET_OFFICE_OPEN_INFO, officeOpenInfo)
		} catch (err) {
			console.error('Failed to fetch office status', err)
			commit(SET_OFFICE_OPEN_INFO, {
				active: false,
				indicator: OFFICE_CLOSED,
				visible: true,
			})
		}
	},

	getAlertPopStatus({ commit, rootState }) {
		let isVisible = rootState.settings.alertSettingsPopup.visible
		let show = { visible: true }
		if (isVisible) {
			show = { visible: false }
		} else {
			show = { visible: true }
		}
		commit(SET_ALERT_SETTING_POPUP, show)
	},

	async setPreference({ rootState, commit }, payload) {
		const userId = rootState.auth?.user?.id
		const response = await api()
			.users()
			.setAlertPreferences(userId, payload)
		commit(SET_ALERT_PREFERENCES, payload)
		return response
	},

	async updatePreference({ rootState, commit }, payload) {
		const userId = rootState.auth?.user?.id
		const {
			id,
			payload: { omd },
		} = payload
		const response = await api()
			.users()
			.updateAlertPreferences(userId, id, { omd: omd || {} })
		commit(SET_ALERT_PREFERENCES, { id, omd })
		return response
	},

	async getAlertSettings({ commit, rootState }) {
		const userId = rootState.auth?.user?.id
		if (!userId) return
		const response = await api()
			.users()
			.getAlertSettings(userId)
		const { omd, id } = response?.response?.data
		commit(SET_ALERT_PREFERENCES, { id, omd })
	},
}
